/**
 * Sizer selections — ONE source, shared by all three sizer forms.
 *
 * The rule (CLAUDE.md): a persisted/posted selection takes its values from the
 * backend, and it must LOOK THE SAME here as it does everywhere else the same
 * field is edited — the Loan Drilldown Details tab, the create-loan form, the
 * property / entity / borrower sidebars.
 *
 * Everywhere else in the portal those lists come from `src/constants/selectOptions/*`,
 * NOT from `useModelFieldOptions`. That matters: several of the curated lists are
 * deliberate SUBSETS of the model enum (loanProductType shows 6 of the model's 9;
 * loanPurpose shows 3 of 4), so reading the live schema in the sizers made the
 * sizers show options no other screen offers. Every value below is still a
 * verbatim model string — the curation only narrows, it never invents.
 *
 * So: re-export the portal constants here rather than duplicating them, and keep
 * the sizer-only vocabularies (which have no portal peer because no other screen
 * edits them) in the same place.
 */
import { selectOptionsLoanProductType } from "../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../constants/selectOptions/selectOptionsLoanPurpose";
import { selectOptionsPropertyType } from "../../constants/selectOptions/selectOptionsPropertyType";
import { selectOptionsPropertyConditionAtOrigination } from "../../constants/selectOptions/selectOptionsPropertyConditionAtOrigination";
import { selectOptionsPropertyStatusAtOrigination } from "../../constants/selectOptions/selectOptionsPropertyStatusAtOrigination";
import { selectOptionsEntityType } from "../../constants/selectOptions/selectOptionsEntityType";
import { selectOptionsCitizenshipStatus } from "../../constants/selectOptions/selectOptionsCitizenshipStatus";
import { selectOptionsPrepaymentPenaltyStructure } from "../../constants/selectOptions/selectOptionsPrepaymentPenaltyStructure";
import { selectOptionsInterestCalcType } from "../../constants/selectOptions/selectOptionsInterestCalcType";
import { selectOptionsAmortizationType } from "../../constants/selectOptions/selectOptionsAmortizationType";
import { selectOptionsUsaStates } from "../../constants/selectOptions/selectOptionsUsaStates";

/* ── Loan-model selections (identical to the drilldown / create-loan) ──────── */
export {
  selectOptionsLoanProductType,
  selectOptionsLoanPurpose,
  selectOptionsPropertyType,
  selectOptionsPropertyConditionAtOrigination,
  selectOptionsPropertyStatusAtOrigination,
  selectOptionsEntityType,
  selectOptionsCitizenshipStatus,
  selectOptionsPrepaymentPenaltyStructure,
  // `interestCalcType` drives the required-liquidity payment calc (the day-count
  // denominator: ~1.4% spread between 30/360 and Actual/365), and
  // `amortizationType` is the CANONICAL DSCR qualifying-payment basis — the same
  // named list the deal field uses, so the sizer and the loan cannot disagree.
  selectOptionsInterestCalcType,
  selectOptionsAmortizationType,
  selectOptionsUsaStates,
};

/* prepaymentPenaltyStructure, NARROWED for the DSCR sizers ───────────────────
 * A deliberate SUBSET of the canonical list, never a rewrite: every surviving
 * value is the model's verbatim string, so a pick still matches server-side by
 * string equality. The two FLAT schedules (5% x5, 1% x5) are dropped — the
 * stepped-down structures are what DSCR actually quotes. Excluding by VALUE
 * rather than re-listing what stays means a new option added to the model flows
 * through on its own. Shared so the portal DSCR sizer and the admin quick-entry
 * sizer cannot drift apart.
 */
export const DSCR_EXCLUDED_PREPAY = ["5% 5% 5% 5% 5%", "1% 1% 1% 1% 1%"];

export const dscrOptionsPrepaymentPenaltyStructure =
  selectOptionsPrepaymentPenaltyStructure.filter(
    (o) => !DSCR_EXCLUDED_PREPAY.includes(o),
  );

/* amortizationType, NARROWED for the DSCR sizers ─────────────────────────────
 * Same rule as the prepay list above: a SUBSET of the canonical loan-model
 * options, every surviving value verbatim, so a pick still matches server-side
 * by string equality.
 *
 * "Interest Only" is dropped. A pure-IO qualifying basis is a committed term
 * rather than something a sizer offers up front — and it is the one selection
 * here that changes the DSCR by changing the PAYMENT (the sheet has its own
 * Interest Only LLPA row, reached through this field), so offering it at
 * scenario stage invites a number the loan will not hold. "10yr IO, 20-Year
 * Amortizing" stays: it is a real product, not the bare IO basis.
 */
export const DSCR_EXCLUDED_AMORTIZATION = ["Interest Only"];

export const dscrOptionsAmortizationType = selectOptionsAmortizationType.filter(
  (o) => !DSCR_EXCLUDED_AMORTIZATION.includes(o),
);

// Exit Strategy is product-type-scoped and HIDDEN for DSCR / Other — re-exported
// so a sizer form gets the same behaviour as the drilldown without importing the
// helper module directly. Use `useExitStrategyOptions` inside an RFF form.
export {
  getExitStrategyOptions,
  isExitStrategyHidden,
} from "../../constants/selectOptions/selectOptionsExitStrategy";

/* ── Sizer-only vocabularies ───────────────────────────────────────────────────
 * These are PRICING-ENGINE selections, not loan-model fields: nothing else in the
 * portal edits them, so there is no `selectOptions*` peer to match. Values are
 * copied verbatim from eastview-os `app/models/pricing_structures.py`.
 */

// ARM_PRODUCT_COLUMNS — the admin-data coupon-ladder columns
// (DscrQuickPriceInput.armProduct).
export const sizerOptionsArmProduct = [
  { value: "fixed_30", label: "Fixed 30" },
  { value: "5_1_arm", label: "5/1 ARM" },
  { value: "7_1_arm", label: "7/1 ARM" },
];

// DscrQuickPriceInput.amortizing is a BOOLEAN, not an enum.
export const sizerOptionsAmortizing = [
  { value: true, label: "Fully Amortizing" },
  { value: false, label: "Interest Only" },
];

// VACANCY_OPTIONS — drives the qualifying-rent basis server-side
// (DscrQuickPriceInput.vacancySelection).
export const sizerOptionsVacancySelection = [
  "Leased",
  "Vacant",
  "Vacation Rental",
  "Partially Leased",
];

// TIER_OPTIONS — Eastview base + correspondent margin tiers.
export const sizerOptionsTier = ["BASE", "GOLD", "SILVER", "BRONZE"];

/**
 * Product types the RTL / GUC sizer can price — a NARROWING of
 * `selectOptionsLoanProductType`, not a different vocabulary: same verbatim
 * model strings, minus the ones this sizer has no business offering.
 *
 *   DSCR              — priced by the DSCR sizer, not this one
 *   Multifamily Bridge— not offered
 *   Other             — not offered
 *
 * Ordered the way the sizer works through them (bridge last).
 */
export const sizerOptionsRtlProductType = [
  "Fix and Flip",
  "Ground Up Construction",
  "Stabilized Bridge",
];

// `termMonths` is a plain integer on the loan model (no enum) — these are the
// terms the sizer offers, matching `selectOptionsRequestedLoanTermMonths`
// (12 / 18 / 24). Nothing longer: RTL loans cap at 24 months.
export const sizerOptionsTermMonths = [12, 18, 24].map((v) => ({
  value: v,
  label: `${v} months`,
}));

// Yes / No for the model's real booleans (floodZone, propertyRuralIndicator,
// crossCollateralized …) — real booleans, not the strings "true"/"false".
export const sizerOptionsYesNo = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

/**
 * exitStrategy — the MODEL's list, copied verbatim from `exit_strategy_options`
 * (eastview_pricing.py / pipeline.py, identical in both).
 *
 * ⚠️ This deliberately does NOT use the portal's `selectOptionsExitStrategy`,
 * which the sizer used until the backend audit (2026-08-17). That helper offers
 * a product-scoped vocabulary — "Rehab to sell", "Bridge to rent", "Build to
 * sell" — that appears NOWHERE in the backend, so every one of its values is a
 * wrong value under the select-options rule, not a nicer caption.
 *
 * The wider question is open and NOT this file's to settle: the drilldown
 * Details tab and the create-loan form use that same helper, and the
 * `exitStrategy` field itself is declared `form_type: 'text'` with no `options`,
 * so nothing rejects the richer values server-side — it is silent data drift,
 * and someone has to decide whether the model adopts the longer list or those
 * screens collapse to these three. Until then the sizer posts model values.
 */
export const sizerOptionsExitStrategy = [
  "Sell",
  "Hold as Rental",
  "Other - Refinance",
];

/**
 * DSCR pricing sheets — used only to NAME the sheet that priced
 * (`result.cpId`). The sizers no longer pick or send a sheet
 * (pricing-scenario-sizers.md, owner 2026-10-08): the server prices the
 * platform sheet, `resfin-base`, on both portals, and falls back to the
 * portal's own base only while resfin-base has no active version.
 */
export const DSCR_EASTVIEW_BASE_SHEET = "eastview-base";
export const DSCR_PLATFORM_BASE_SHEET = "resfin-base";

export const dscrOptionsPricingSheet = [
  { value: DSCR_EASTVIEW_BASE_SHEET, label: "Eastview base" },
  { value: DSCR_PLATFORM_BASE_SHEET, label: "Resfin base (platform)" },
];

/** Display name for a sheet id; an unknown counterparty sheet shows its id. */
export const dscrSheetLabel = (cpId) =>
  dscrOptionsPricingSheet.find((o) => o.value === cpId)?.label || cpId || null;
